import React, { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import {
  TrendingUp, AlertTriangle, DollarSign, ChevronRight, ChevronLeft, ArrowRight,
  Activity, Zap, Target, BarChart3, Shield, CheckCircle2, X,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../context/ThemeContext';
import GoalRing from './GoalRing';
import LEIActionPlanCard from '../lei/LEIActionPlanCard';
import { WeakestMeasureTile, buildLeiFocus } from '../health/HealthActionSpine';
import PeerBenchmarkBar from '../common/PeerBenchmarkBar';
import PeerBenchmarkTrend from '../common/PeerBenchmarkTrend';

// ── colour + band helpers (mirror Maturity; light variants via --bl) ──
const barColor = (s) => (s >= 75 ? '#06b6d4' : s >= 60 ? '#14b8a6' : s >= 45 ? '#f59e0b' : '#94a3b8');
const barColorLight = (s) => (s >= 75 ? '#0e7490' : s >= 60 ? '#0f766e' : s >= 45 ? '#b45309' : '#475569');
const band = (s) => (s >= 80 ? 'Strong' : s >= 65 ? 'Good' : s >= 45 ? 'Developing' : 'Emerging');
const scoreText = (s) => (s >= 75 ? 'text-cyan-300' : s >= 60 ? 'text-teal-300' : s >= 45 ? 'text-amber-300' : 'text-slate-300');
const scoreStroke = (s) => barColor(s);
const bandChip = (s) => (
  s >= 80 ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
    : s >= 65 ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'
      : s >= 45 ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
        : 'bg-slate-500/10 border-slate-500/30 text-slate-300'
);
const PILLAR_ICONS = { P1: Activity, P2: Zap, P3: Target, P4: BarChart3, P5: Shield };
const fmtMoney = (n) => (n >= 1000 ? `$${Math.round(n / 1000)}K` : `$${Math.round(n)}`);

export function DarkCard({ className = '', children, testId }) {
  return <div className={cn('rounded-2xl bg-[#111827] ring-1 ring-[#1f2a44] shadow-xl', className)} data-testid={testId}>{children}</div>;
}

function ScoreGauge({ score, size = 80 }) {
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r="42" fill="none" stroke="#1f2a44" strokeWidth="8" />
        <circle cx="50" cy="50" r="42" fill="none" stroke={scoreStroke(score)} strokeWidth="8"
          strokeDasharray={`${Math.max(0, Math.min(100, score)) * 2.64} 264`} strokeLinecap="round" />
      </svg>
      <div className={cn('absolute inset-0 flex items-center justify-center text-lg font-bold', scoreText(score))}>{Math.round(score)}</div>
    </div>
  );
}

// Derived KPIs from the real overview payload (honest, computed).
function deriveKpis(data) {
  const score = Math.round(data?.overall_score || 0);
  const inv = data?.investment_data || {};
  const total = inv.total_investment || 0;
  const gap = Math.max(0, 100 - score);
  const uplift = Math.max(0, Math.round(gap * 0.5));
  const nextBand = score >= 80 ? 'Optimized' : score >= 65 ? 'Managed' : score >= 45 ? 'Established' : 'Developing';
  const valueLow = total * (gap / 100) * 0.24;
  const valueHigh = total * ((inv.roi_percentage || 100) / 1000);
  const efficiency = Math.min(100, Math.round(score + (inv.efficiency_trend || 0) * 0.4));
  return { score, uplift, nextBand, valueLow, valueHigh, efficiency };
}

function KpiTiles({ data, weakest, navigate }) {
  const { score, uplift, nextBand, valueLow, valueHigh, efficiency } = deriveKpis(data);
  const trend = data?.overall_trend ?? 0;
  const risks = (data?.risks || []).length;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="lei-kpi-tiles">
      <DarkCard className="p-5" testId="lei-kpi-score">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">LEI Score</div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className={cn('text-4xl font-heading font-extrabold tabular-nums', scoreText(score))}>{score}</span>
              <span className="text-sm text-slate-500 font-semibold">/100</span>
            </div>
            <span className={cn('mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border', bandChip(score))}>{band(score)}</span>
            <div className={cn('mt-2 flex items-center gap-1 text-[10px] font-bold', trend > 0 ? 'text-emerald-400' : trend < 0 ? 'text-rose-400' : 'text-slate-500')}>
              {trend > 0 ? '▲' : trend < 0 ? '▼' : '▬'} {trend > 0 ? '+' : ''}{trend} vs last quarter
            </div>
          </div>
          <div className="flex flex-col items-center gap-1.5">
            <ScoreGauge score={score} />
            <GoalRing current={score} entityId="portfolio" isDark size={30} testId="overview-goal-ring" />
          </div>
        </div>
      </DarkCard>

      <DarkCard className="p-5 relative overflow-hidden ring-1 ring-rose-500/40 mc-priority-glass shadow-lg shadow-rose-900/20" testId="lei-kpi-priority">
        <div className="absolute -top-6 -right-6 h-20 w-20 rounded-full bg-rose-500/10 blur-2xl" />
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-rose-300"><AlertTriangle className="h-3.5 w-3.5 text-rose-400" /> Top Priority</div>
        <p className="mt-2 text-2xl font-extrabold text-white leading-tight">{weakest ? weakest.name : '—'}</p>
        {weakest && <p className="mt-1 text-sm text-rose-300 font-semibold tabular-nums">Score {Math.round(weakest.score)}<span className="text-slate-400 font-normal"> / 100</span></p>}
        <p className="text-xs text-slate-400 mt-0.5">{risks} active risk{risks === 1 ? '' : 's'}</p>
        {weakest && navigate && (
          <button onClick={() => navigate(`/lei-health/priorities?focus=action-plan`)} data-testid="lei-kpi-priority-action"
            className="mt-4 w-full justify-center inline-flex items-center gap-2 text-sm font-bold px-3 py-2.5 rounded-lg bg-rose-500 text-white mc-priority-cta hover:bg-rose-400 shadow-lg shadow-rose-500/30 transition-colors">
            <Zap className="h-4 w-4" /> Take Action <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </DarkCard>

      <DarkCard className="p-5" testId="lei-kpi-uplift">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500"><TrendingUp className="h-3.5 w-3.5 text-emerald-400" /> Uplift Potential</div>
        <p className="mt-2 text-3xl font-heading font-extrabold text-emerald-300 tabular-nums">+{uplift}% ROI</p>
        <p className="text-xs text-slate-400 mt-1">When advancing to {nextBand}</p>
      </DarkCard>

      <DarkCard className="p-5" testId="lei-kpi-value">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500"><DollarSign className="h-3.5 w-3.5 text-rose-400" /> Value at Stake</div>
        <p className="mt-2 text-2xl font-heading font-extrabold text-rose-300 tabular-nums">{fmtMoney(valueLow)} – {fmtMoney(valueHigh)}</p>
        <p className="text-xs text-slate-400 mt-1">Yearly value at risk</p>
      </DarkCard>

      <DarkCard className="p-5" testId="lei-kpi-efficiency">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500"><DollarSign className="h-3.5 w-3.5 text-[#44b6d2]" /> Investment Efficiency</div>
        <p className="mt-2 text-3xl font-heading font-extrabold text-cyan-300 tabular-nums">{efficiency}%</p>
        <p className="text-xs text-slate-400 mt-1">Effective utilization</p>
      </DarkCard>
    </div>
  );
}

function PillarBreakdown({ pillars, weakestId, onSelect }) {
  const rows = [...pillars].sort((a, b) => b.score - a.score);
  return (
    <DarkCard className="p-5" testId="lei-pillar-breakdown">
      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Pillar Breakdown</div>
      <p className="text-xs text-slate-500 mt-1 mb-4">Click a pillar to see its impact on your LEI → KPI → ROI chain</p>
      <div className="space-y-1">
        {rows.map((p) => {
          const Icon = PILLAR_ICONS[p.id] || Activity;
          const c = barColor(p.score);
          const isWeak = p.id === weakestId;
          return (
            <button key={p.id} onClick={() => onSelect(p.id)} className="w-full group flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.04] transition-colors text-left" data-testid={`lei-pillar-row-${p.id}`}>
              <span className="shrink-0 w-8 h-8 rounded-lg bg-[#0b1220] ring-1 ring-[#22304d] flex items-center justify-center"><Icon className="h-4 w-4 text-[#44b6d2]" /></span>
              <div className="min-w-0 w-36 sm:w-44 shrink-0">
                <p className="text-sm font-semibold text-slate-100 truncate">{p.short_name || p.name}</p>
                <p className="text-[11px] font-medium mc-band" style={{ color: c, '--bl': barColorLight(p.score) }}>{band(p.score)}</p>
              </div>
              <div className="flex-1 h-2 rounded-full bg-[#0b1220] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${p.score}%`, backgroundColor: c }} /></div>
              {isWeak && <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">Constraint</span>}
              <span className="shrink-0 w-8 text-right text-lg font-bold tabular-nums mc-band" style={{ color: c, '--bl': barColorLight(p.score) }}>{Math.round(p.score)}</span>
              <ChevronRight className="h-4 w-4 text-slate-600 group-hover:text-slate-300 transition-colors shrink-0" />
            </button>
          );
        })}
      </div>
    </DarkCard>
  );
}

const LEI_PILLAR_LINES = [
  { key: 'P1', color: '#38bdf8' },
  { key: 'P2', color: '#34d399' },
  { key: 'P3', color: '#fbbf24' },
  { key: 'P4', color: '#f472b6' },
  { key: 'P5', color: '#a78bfa' },
];
// Legend/tooltip ordering: Overall LEI (score) leads, then pillars.
const LEGEND_RANK = { score: 0, P1: 1, P2: 2, P3: 3, P4: 4, P5: 5 };

// Marker rendered at the top of an annotation ReferenceLine. A dot +
// label sit at the top of the plot; a full-height transparent hit
// area carries a native SVG <title> so hovering the line shows a
// small tooltip with a one-line description of what changed.
function AnnotationMarker({ viewBox, anno, color, index, last, onClick }) {
  if (!viewBox) return null;
  const { x, y, height } = viewBox;
  // Keep the on-chart label short (full text lives in the <title>
  // hover tooltip); anchor the last marker to the left of its line
  // so it doesn't clip against the card's right edge.
  const short = anno.label.length > 14 ? `${anno.label.slice(0, 13)}…` : anno.label;
  const anchor = last ? 'end' : 'middle';
  const tx = last ? x - 6 : x;
  // Stagger adjacent labels vertically so markers in neighbouring
  // months don't collide near the right edge (kept within the chart's
  // top margin so labels aren't clipped by the SVG edge).
  const ty = (index % 2 === 0 ? y - 4 : y - 17);
  const clickable = !!onClick;
  return (
    <g data-testid={`lei-trend-annotation-${index}`} onClick={onClick} style={{ cursor: clickable ? 'pointer' : 'default' }}>
      <title>{`${anno.label}${anno.detail ? ` — ${anno.detail}` : ''}${clickable ? ' (click to open)' : ''}`}</title>
      <rect x={x - 8} y={y} width={16} height={height} fill="transparent" pointerEvents="all" />
      <circle cx={x} cy={y + 4} r={4} fill={color} stroke={clickable ? '#0b1220' : 'none'} strokeWidth={clickable ? 1 : 0} />
      <text x={tx} y={ty} textAnchor={anchor} fontSize={9} fontWeight={600} fill={color}>{short}</text>
    </g>
  );
}

// Launch vs content-refresh markers read differently at a glance.
const ANNO_COLOR = { program: '#22d3ee', content: '#f59e0b', mixed: '#a78bfa' };
const ANNO_LABELS = { all: 'All', program: 'Launches', content: 'Content', compliance: 'Compliance', leadership: 'Leadership', technical: 'Technical', onboarding: 'Onboarding' };

// Collapse a filtered list of raw events into one marker descriptor.
function describeEvents(events) {
  const types = new Set(events.map((e) => e.type));
  const type = types.size === 1 ? [...types][0] : 'mixed';
  const uniqNames = [...new Set(events.map((e) => e.name))];
  let label; let detail;
  if (events.length === 1) { label = events[0].name; detail = events[0].detail; }
  else if (uniqNames.length === 1) { label = `${uniqNames[0]} ×${events.length}`; detail = `${events[0].detail} (${events.length} this month)`; }
  else { label = `${events.length} events`; detail = uniqNames.join('; '); }
  const firstProgram = events.find((e) => e.program_id);
  return { type, label, detail, target: firstProgram ? { kind: 'program', id: firstProgram.program_id } : (type === 'content' ? { kind: 'content' } : null) };
}

export function LeiTrend({ trendData, pillars, annotations, navigate }) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== 'light';
  const overallColor = isDark ? '#f8fafc' : '#0f172a';
  const gridColor = isDark ? '#1f2a44' : '#e2e8f0';
  const axisColor = isDark ? '#94a3b8' : '#64748b';
  const trendCardCls = isDark
    ? 'rounded-2xl bg-[#111827] ring-1 ring-[#1f2a44] shadow-xl'
    : 'rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm';
  // Click a legend entry to hide/show that series (de-clutter).
  const [hidden, setHidden] = useState(() => new Set());
  const [annoFilter, setAnnoFilter] = useState('all');
  const [picker, setPicker] = useState(null);
  const toggle = (key) => setHidden((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const nameOf = (key) => {
    const p = (pillars || []).find((x) => x.id === key);
    return p?.short_name || p?.name || key;
  };
  const data = (trendData || []).map((t) => {
    const row = { month: t.month, score: Math.round(t.score) };
    LEI_PILLAR_LINES.forEach(({ key }) => {
      if (typeof t[key] === 'number') row[key] = Math.round(t[key]);
    });
    return row;
  });
  const activePillars = LEI_PILLAR_LINES.filter(({ key }) => data.length && typeof data[0][key] === 'number');
  const visibleKeys = ['score', ...activePillars.map((p) => p.key)].filter((k) => !hidden.has(k));
  const vals = data.flatMap((r) => visibleKeys.map((k) => r[k]).filter((v) => typeof v === 'number'));
  const domain = vals.length
    ? [Math.max(0, Math.floor(Math.min(...vals)) - 4), Math.min(100, Math.ceil(Math.max(...vals)) + 4)]
    : [0, 100];

  // Available annotation filter categories (from real event data).
  const allEvents = (annotations || []).flatMap((a) => a.events || []);
  const categories = ['all',
    ...[...new Set(allEvents.filter((e) => e.type === 'program' && e.program_type).map((e) => e.program_type))],
    ...(allEvents.some((e) => e.type === 'content') ? ['content'] : []),
  ];
  const matchesFilter = (e) => annoFilter === 'all'
    || (annoFilter === 'content' ? e.type === 'content' : e.program_type === annoFilter);
  // Build the (filtered) marker list, recomputing each month's label,
  // colour and click target from the events that survive the filter.
  const annos = (annotations || [])
    .filter((a) => data.some((d) => d.month === a.month))
    .map((a) => {
      const evs = (a.events || []).filter(matchesFilter);
      if (!evs.length) return null;
      return { month: a.month, events: evs, ...describeEvents(evs) };
    })
    .filter(Boolean);

  const onMarkerClick = (a) => {
    if (!navigate) return;
    const programs = (a.events || []).filter((e) => e.program_id);
    // Several programs launched this month → let the user pick one.
    if (programs.length > 1) { setPicker({ month: a.month, events: programs }); return; }
    if (a.target?.kind === 'program') navigate(`/program-analytics?program_id=${encodeURIComponent(a.target.id)}`);
    else if (a.target?.kind === 'content') navigate('/data-management');
  };

  return (
    <div className={cn(trendCardCls, 'p-5')} data-testid="lei-trend">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400"><TrendingUp className="h-4 w-4 text-[#44b6d2]" /> Learning Health Trends</div>
        <span className="text-[10px] text-slate-500">Click legend to toggle</span>
      </div>
      {categories.length > 1 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="lei-trend-anno-filter">
          <span className="text-[10px] uppercase tracking-wide text-slate-500 mr-1">Events</span>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setAnnoFilter(cat)}
              data-testid={`lei-trend-anno-filter-${cat}`}
              className={cn(
                'text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors',
                annoFilter === cat
                  ? 'bg-[#44b6d2] text-[#04222b] border-[#44b6d2]'
                  : 'bg-transparent text-slate-400 border-[#22304d] hover:border-[#44b6d2] hover:text-slate-200',
              )}
            >{ANNO_LABELS[cat] || cat.charAt(0).toUpperCase() + cat.slice(1)}</button>
          ))}
        </div>
      )}
      {data.length >= 2 ? (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data} margin={{ top: 34, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="month" tick={{ fill: axisColor, fontSize: 11 }} stroke={gridColor} />
            <YAxis domain={domain} allowDecimals={false} tick={{ fill: axisColor, fontSize: 11 }} stroke={gridColor} />
            <RTooltip
              contentStyle={isDark
                ? { background: '#0b1220', border: '1px solid #22304d', borderRadius: 8, color: '#e2e8f0' }
                : { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, color: '#0f172a' }}
              itemSorter={(item) => (item.dataKey === 'score' ? -1 : 1)}
            />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 8, cursor: 'pointer' }}
              iconType="plainline"
              itemSorter={(item) => (LEGEND_RANK[item?.dataKey ?? item?.payload?.dataKey] ?? 99)}
              onClick={(o) => toggle(o?.dataKey || o?.payload?.dataKey)}
              formatter={(value, entry) => {
                const key = entry?.dataKey || entry?.payload?.dataKey;
                return <span style={{ opacity: hidden.has(key) ? 0.4 : 1 }} data-testid={`lei-trend-legend-${key}`}>{value}</span>;
              }}
            />
            {/* Event markers — colored by kind (launch vs content),
                filterable, and clickable through to the program. */}
            {annos.map((a, i) => {
              const color = ANNO_COLOR[a.type] || ANNO_COLOR.mixed;
              return (
                <ReferenceLine
                  key={`${a.month}-${i}`}
                  x={a.month}
                  stroke={color}
                  strokeDasharray="4 3"
                  label={<AnnotationMarker anno={a} color={color} index={i} last={i === annos.length - 1} onClick={(a.target || (a.events || []).some((e) => e.program_id)) ? () => onMarkerClick(a) : undefined} />}
                />
              );
            })}
            {/* Overall LEI rendered first so it leads the legend + tooltip. */}
            <Line type="monotone" dataKey="score" name="Overall LEI" stroke={overallColor} strokeWidth={3} dot={{ r: 3, fill: overallColor }} activeDot={{ r: 5 }} hide={hidden.has('score')} />
            {activePillars.map(({ key, color }) => (
              <Line key={key} type="monotone" dataKey={key} name={nameOf(key)} stroke={color} strokeWidth={2} strokeDasharray="5 4" dot={false} activeDot={{ r: 4 }} hide={hidden.has(key)} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      ) : <p className="text-sm text-slate-500 py-10 text-center">Not enough history yet to plot a trend.</p>}
      {annos.length > 0 && (
        <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500" data-testid="lei-trend-marker-key">
          <span className="uppercase tracking-wide">Markers</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: ANNO_COLOR.program }} />Launch</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: ANNO_COLOR.content }} />Content refresh</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: ANNO_COLOR.mixed }} />Mixed</span>
        </div>
      )}
      {picker && (
        <div className="mt-3 rounded-xl bg-[#0b1220] ring-1 ring-[#22304d] p-3" data-testid="lei-trend-event-picker">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-300">{picker.month} · pick a program</p>
            <button onClick={() => setPicker(null)} className="text-slate-500 hover:text-slate-200" data-testid="lei-trend-event-picker-close"><X className="h-4 w-4" /></button>
          </div>
          <div className="space-y-1">
            {picker.events.map((e) => (
              <button
                key={e.program_id}
                onClick={() => { setPicker(null); navigate(`/program-analytics?program_id=${encodeURIComponent(e.program_id)}`); }}
                className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-white/[0.05] text-left transition-colors"
                data-testid={`lei-trend-event-pick-${e.program_id}`}
              >
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: ANNO_COLOR.program }} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-slate-100 truncate">{e.name}</span>
                  {e.detail && <span className="block text-[11px] text-slate-500 truncate">{e.detail}</span>}
                </span>
                <ChevronRight className="h-4 w-4 text-slate-600 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AlertsStrip({ data, weakest, navigate }) {
  const cells = [];
  if (weakest) cells.push({ label: weakest.name, sub: 'Lowest scoring pillar', value: `${Math.round(weakest.score)}/100`, tone: 'text-rose-300' });
  (data?.risks || []).slice(0, 3).forEach((r) => cells.push({ label: r.measure, sub: (r.description || '').slice(0, 40), value: (r.severity || 'medium').replace(/^\w/, (c) => c.toUpperCase()), tone: r.severity === 'high' ? 'text-rose-300' : 'text-amber-300' }));
  const allClear = cells.length === 0;
  return (
    <DarkCard className="px-5 py-4" testId="lei-recent-alerts">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <span className={cn('w-8 h-8 rounded-lg ring-1 flex items-center justify-center', allClear ? 'bg-emerald-500/15 ring-emerald-500/30' : 'bg-rose-500/15 ring-rose-500/30')}>
            {allClear ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-rose-400" />}
          </span>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-300">Recent Alerts</p>
            <p className="text-xs text-slate-500">{allClear ? 'No active risks flagged.' : 'Your learning gaps require attention.'}</p>
          </div>
        </div>
        <button onClick={() => navigate('/lei-health/insights')} className="text-xs font-semibold text-[#44b6d2] hover:text-cyan-300 inline-flex items-center gap-1 transition-colors" data-testid="lei-alerts-view-all">View all alerts <ArrowRight className="h-3.5 w-3.5" /></button>
        <div className="flex-1 grid grid-cols-2 lg:grid-cols-4 gap-3 min-w-[260px]">
          {cells.slice(0, 4).map((c, i) => (
            <div key={i} className="rounded-xl bg-[#0b1220] ring-1 ring-[#22304d] px-3 py-2" data-testid={`lei-alert-cell-${i}`}>
              <p className="text-[11px] font-semibold text-slate-200 truncate">{c.label}</p>
              <p className="text-[10px] text-slate-500 truncate">{c.sub}</p>
              <p className={cn('text-sm font-bold mt-0.5', c.tone)}>{c.value}</p>
            </div>
          ))}
        </div>
      </div>
    </DarkCard>
  );
}

export default function LeiLeanOverview({ data, navigate, selectedOrg }) {
  const pillars = data?.pillars || [];
  const weakest = pillars.length ? [...pillars].sort((a, b) => a.score - b.score)[0] : null;
  return (
    <div className="space-y-4" data-testid="lei-overview">
      <KpiTiles data={data} weakest={weakest} navigate={navigate} />
      <WeakestMeasureTile focus={buildLeiFocus(data)} onView={() => navigate('/lei-health/priorities')} testId="lei-weakest-measure-tile" />
      <AlertsStrip data={data} weakest={weakest} navigate={navigate} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        <div className="lg:col-span-7"><PillarBreakdown pillars={pillars} weakestId={weakest?.id} onSelect={(id) => navigate(`/lei-health/deep-dives/${id}`)} /></div>
        <div className="lg:col-span-5"><LeiTrend trendData={data?.trend_data} pillars={data?.pillars} annotations={data?.trend_annotations} navigate={navigate} /></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        <DarkCard className="p-5 lg:col-span-7" testId="lei-overview-action-plan">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">Action Plan</div>
          {weakest?.id ? <LEIActionPlanCard pillarId={weakest.id} scoreOverride={weakest.score} overallScore={data?.overall_score} /> : <p className="text-sm text-slate-500">No priority pillar identified.</p>}
        </DarkCard>
        <DarkCard className="p-5 lg:col-span-5" testId="lei-peer">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">Peer Benchmark · LEI</div>
          {data?.peer_benchmark ? (
            <div className="space-y-2 text-sm">
              <div className="flex items-baseline gap-2"><span className="text-3xl font-extrabold tabular-nums text-cyan-300">{data.peer_benchmark.percentile}th</span><span className="text-xs text-slate-400">percentile · {data.peer_benchmark.performance_label}</span></div>
              <p className="text-xs text-slate-400">You: <span className="font-semibold text-slate-200">{Math.round(data.overall_score)}</span> · Peer median: <span className="font-semibold text-slate-200">{Math.round(data.peer_benchmark.peer_median)}</span></p>
              <PeerBenchmarkBar yourValue={data.overall_score} bench={data.peer_benchmark} testId="lei-benchmark-bar" />
              <p className="text-[11px] text-slate-500">{data.peer_benchmark.basis}</p>
              <PeerBenchmarkTrend stage="learning" organizationId={selectedOrg} testId="lei-benchmark-trend" />
            </div>
          ) : <p className="text-sm text-slate-500">No benchmark available.</p>}
        </DarkCard>
      </div>
    </div>
  );
}

export function LeiDeepDive({ data, pillarId, navigate }) {
  const pillars = data?.pillars || [];
  const p = pillars.find((x) => x.id === pillarId);
  const weakestId = pillars.length ? [...pillars].sort((a, b) => a.score - b.score)[0].id : null;
  if (!p) return <div className="text-sm text-slate-400">Pillar not found. <button className="text-[#44b6d2]" onClick={() => navigate('/lei-health/deep-dives')}>Back</button></div>;
  const Icon = PILLAR_ICONS[p.id] || Activity;
  const c = barColor(p.score);
  return (
    <div className="space-y-4" data-testid="lei-deep-dive">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => navigate('/lei-health/deep-dives')} className="text-xs text-slate-400 hover:text-slate-100 inline-flex items-center gap-1"><ChevronLeft className="h-3.5 w-3.5" /> All pillars</button>
        <span className="text-slate-700">·</span>
        {pillars.map((x) => (
          <button key={x.id} onClick={() => navigate(`/lei-health/deep-dives/${x.id}`)} className={cn('text-[11px] px-2.5 py-1 rounded-full transition-colors', x.id === pillarId ? 'bg-[#44b6d2] text-[#04222b] font-semibold' : 'text-slate-400 hover:bg-white/5')} data-testid={`lei-dd-switch-${x.id}`}>{x.short_name || x.name}</button>
        ))}
      </div>
      <DarkCard className="p-5">
        <div className="flex items-start gap-4 flex-wrap">
          <span className="w-12 h-12 rounded-xl bg-[#0b1220] ring-1 ring-[#22304d] flex items-center justify-center shrink-0"><Icon className="h-6 w-6 text-[#44b6d2]" /></span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-slate-100">{p.name}</h2>
              {p.id === weakestId && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">Primary Constraint</span>}
              <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full border', bandChip(p.score))}>{band(p.score)}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Weight in overall LEI: {Math.round((p.weight || 0) * 100)}% · Trend {p.trend > 0 ? '+' : ''}{p.trend}</p>
            <div className="mt-3 h-2 rounded-full bg-[#0b1220] overflow-hidden max-w-md"><div className="h-full rounded-full" style={{ width: `${p.score}%`, backgroundColor: c }} /></div>
          </div>
          <ScoreGauge score={p.score} size={88} />
        </div>
      </DarkCard>
      {(p.measures || []).length > 0 && (
        <DarkCard className="p-5">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#44b6d2] mb-3">Underlying Measures</div>
          <div className="space-y-2">
            {p.measures.map((m, i) => (
              <div key={i} className="flex items-center justify-between gap-3 rounded-lg bg-[#0b1220] ring-1 ring-[#22304d] px-3 py-2">
                <span className="text-sm text-slate-200 truncate">{m.name || m.measure}</span>
                <span className="text-sm font-bold tabular-nums mc-band" style={{ color: barColor(m.score || 0), '--bl': barColorLight(m.score || 0) }}>{Math.round(m.score || 0)}</span>
              </div>
            ))}
          </div>
        </DarkCard>
      )}
      <DarkCard className="p-5" testId="lei-dd-action-plan">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">Action Plan</div>
        <LEIActionPlanCard pillarId={p.id} scoreOverride={p.score} overallScore={data?.overall_score} />
      </DarkCard>
    </div>
  );
}
